import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAnalytics,
  isSupported,
  type Analytics,
} from 'firebase/analytics';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const hasRequiredConfig = Boolean(
  firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId
);

let app: FirebaseApp | undefined;
let analytics: Analytics | undefined;
let analyticsReady: Promise<Analytics | undefined> = Promise.resolve(undefined);

const enableDebugMode = (): void => {
  if (process.env.NODE_ENV !== 'development') return;
  if (typeof document === 'undefined') return;
  document.cookie = 'FIREBASE_ANALYTICS_DEBUG_MODE=true; path=/; SameSite=Strict';
  const measurementId = firebaseConfig.measurementId;
  if (!measurementId) return;
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag === 'function') {
    gtag('config', measurementId, { debug_mode: true });
  }
};

if (hasRequiredConfig) {
  app = initializeApp(firebaseConfig);

  if (typeof window !== 'undefined') {
    enableDebugMode();
    analyticsReady = isSupported()
      .then((supported) => {
        if (!supported || !app) return undefined;
        analytics = getAnalytics(app);
        enableDebugMode();
        return analytics;
      })
      .catch(() => undefined);
  }
} else if (process.env.NODE_ENV === 'development') {
  console.warn(
    'Firebase Analytics is not configured. Set REACT_APP_FIREBASE_* in .env to enable it.'
  );
}

export { app, analytics, analyticsReady };
